// import { StripePaymentElementOptions } from '@stripe/stripe-js';
import { Modal } from 'antd';
import clsx from 'clsx';
import { motion } from 'framer-motion';
import mixpanel from 'mixpanel-browser';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';

// import StripeMain from '@/components/Stripe';
import { cartAtom } from '@/atoms/Cart';
import { countryAtom } from '@/atoms/countryState';

// import { getSyncBookListApi } from '@/services/Library/Books';
import { usePayment } from '../RazorPay';
import ButtonDefault, { ButtonVariants } from '../../shared/basic/button';
import { ShouldRender } from '../../shared/basic/ShouldRender';
import { formateErrorObject } from '../../shared/formateErrorObject';
import { notify } from '../../shared/notify';
import ExclamationMarkCart from '../../../../assets/Icons/ExclamationMarkCart';
import { user } from '../../../atoms/user';
import { EventDetails, logEvent } from '../../../hooks/useClickEventTracking';
import {
  // createRazorpayPayment,
  getOrderDetails,
  handleConvertToCod,
} from '../../../services/Cart';
import {
  FormattedFinalOrder,
  RawOrderData,
} from '../../../utils/interfaces/Orders';
import { User } from '../../../utils/interfaces/User';

interface Data {
  amount: {
    subTotal: number;
    discount: string;
    total: number;
    tax: number;
    couponCode: { coupon_code: string }[];
  };
  razorpayOrderId: string;
  orderId: string;
  userData: User;
  setVisible: (visible: boolean) => void;
  paymentType: string;
}

interface Props {
  visible: boolean;
  closeModal: () => void;
  data: Data;
  orderApiData: any;
  options?: any;
}

const DotLoader = () => {
  return (
    <motion.span
      className='ml-1'
      initial={{ opacity: 0.2 }}
      animate={{ opacity: 1 }}
      transition={{
        repeat: Infinity,
        repeatType: 'mirror',
        duration: 1,
      }}
    >
      ...
    </motion.span>
  );
};

const CloseModal: React.FC<Props> = ({
  closeModal,
  visible,
  data,
  // orderApiData,
  // options,
}) => {
  const [countryState] = useRecoilState(countryAtom);
  // const [stripeModal, setStripeModal] = useState<boolean>(false);
  const [order, setOrder] = useState<FormattedFinalOrder>();
  const [loadingOrder, setLoadingOrder] = useState(false);
  const [convertCodLoading, setConvertCodLoading] = useState(false);
  const cartData = useRecoilValue(cartAtom);
  const userData: User | undefined = useRecoilValue(user);
  const environment = process.env.NEXT_PUBLIC_APP_ENV;
  const { initializeRazorpay } = usePayment();
  const router = useRouter();
  const cod_limit =
    Number(process.env.NEXT_PUBLIC_ALLOWABLE_COD_AMOUNT) || 12000;

  const { od, cartId, pre } = router.query;

  const handleCloseModal = () => {
    localStorage.removeItem('tempCartData');
    closeModal();
  };
  useEffect(() => {
    if (od) {
      handleFinalCartDetails(od as string);
    }
  }, [od]);

  useEffect(() => {
    if (cartData && cartData.length > 0) {
      localStorage.setItem('tempCartData', JSON.stringify(cartData));
    }
  }, [cartData]);

  const hasNoCOD = useMemo(() => {
    if (typeof window === 'undefined') return false;

    const saved = localStorage.getItem('tempCartData');
    if (!saved) return false;

    try {
      const parsed = JSON.parse(saved);
      if (!Array.isArray(parsed)) return false;

      return parsed.some((item) => item?.cash_on_delivery === false);
    } catch (err) {
      console.error('Error parsing tempCartData:', err);
      return false;
    }
  }, []);

  useEffect(() => {
    const handleBackButton = (event: PopStateEvent) => {
      if (visible) {
        event.preventDefault();
        window.history.pushState(null, '', window.location.href);
      }
    };

    if (visible) {
      const eventParams: EventDetails = {
        event_name: 'screen_view',
        source: 'global',
        source_type: 'retry_popup',
        source_id: order?.orderId as string,
        sub_source: null,
        sub_source_id: null,
        unit: 'boolean',
        value: 1,
        meta: {
          click_source: 'retry_popup',
        },
      };

      logEvent({
        eventDetails: eventParams,
        userData: userData,
        pathname: router.pathname,
      });
      window.history.pushState(null, '', window.location.href);

      window.addEventListener('popstate', handleBackButton);

      const intervalId = setInterval(() => {
        window.history.pushState(null, '', window.location.href);
      }, 100);

      return () => {
        clearInterval(intervalId);
        window.removeEventListener('popstate', handleBackButton);
      };
    }
    return;
  }, [visible]);

  const productIds = (order?.orderDetails || [])
    .map((detail: any) => detail?.id)
    .join(',');

  const convertToCod = async () => {
    try {
      setConvertCodLoading(true);
      await handleConvertToCod(
        {
          order_id: order?.orderId as string,
        },
        (cartData[0]?.cartId as number) || Number(cartId),
      );

      const eventParams: EventDetails = {
        event_name: 'convert_cod',
        source: 'global',
        source_type: 'orders',
        source_id: order?.orderId as string,
        sub_source: null,
        sub_source_id: null,
        unit: order?.currency_code || 'INR',
        currency: order?.currency_code || 'INR',
        value: order?.grand_total,
        meta: {
          click_source: 'retry_popup',
          payment_mode: order?.payment_type === 'razorpay' ? 'prepaid' : 'cod',
        },
      };

      logEvent({
        eventDetails: eventParams,
        userData: userData,
        pathname: router.pathname,
      });

      if (typeof mixpanel !== 'undefined' && environment === 'production') {
        mixpanel.track(eventParams.event_name as string, eventParams);
      }
      if (typeof window !== 'undefined') {
        router.replace({
          pathname: '/order-successful',
          query: {
            orderId: od,
            product_ids: productIds,
            callPurchased: 'purchased',
          },
        });
        window.history.replaceState(null, '', '/order-successful');
      }
    } catch (error: any) {
      const message = formateErrorObject(error);
      notify(message, 'error');
    } finally {
      setConvertCodLoading(false);
    }
  };

  const formatOrderDetails = (
    data: RawOrderData | null,
  ): FormattedFinalOrder | null => {
    if (!data) return null;

    const formattedOrder: FormattedFinalOrder = {
      orderId: data.id.toString(),
      transactionId: data.payment_details?.rzp_order_id || '',
      grand_total: data.grand_total || 0,
      payment_type: data.payment_type,
      currency: data?.currency_symbol || '₹',

      orderDetails: data.order_details.map((item) => ({
        id: item.id.toString(),
        name: item.product.name,
        quantity: item.quantity,
        product_id: item.product_id,
        price: item.product.main_price,
        image: item.product.thumbnail_image,
        currency_symbol: item?.product?.currency_symbol,
        book_type: item?.product?.book_type,
      })),
    };

    return formattedOrder;
  };

  const handleFinalCartDetails = async (id: string) => {
    try {
      setLoadingOrder(true);
      if (id) {
        const data = await getOrderDetails(id);
        const formattedData = formatOrderDetails(data);
        setOrder(formattedData as FormattedFinalOrder);
      }
    } catch (error: any) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error initializing messaging:', error);
      }
    } finally {
      setLoadingOrder(false);
    }
  };

  const handlePayment = async () => {
    handleCloseModal();
    if (countryState.selectedCountry?.code === 'IN') {
      await initializeRazorpay({
        totalAmount: order?.grand_total || 0,
        razorpayOrderId: order?.transactionId || '',
        orderId: order?.orderId || '',

        userData: {
          ...userData,
          name: data?.userData?.name ?? userData?.name ?? '',
          email: data?.userData?.email ?? userData?.email ?? '',
        } as User,
        setVisible: data.setVisible,
        paymentType: 'razor-pay',
        cartId: (cartData[0]?.cartId as number) || Number(cartId),
        isCartPage: true,
      });

      const eventParams: EventDetails = {
        event_name: 'retry_payment',
        source: 'global',
        source_type: 'orders',
        source_id: order?.orderId as string,
        sub_source: null,
        sub_source_id: null,
        unit: order?.currency_code || 'INR',
        currency: order?.currency_code || 'INR',
        value: order?.grand_total,
        meta: {
          click_source: 'retry_popup',
          payment_mode: order?.payment_type === 'razorpay' ? 'prepaid' : 'cod',
        },
      };

      logEvent({
        eventDetails: eventParams,
        userData: userData,
        pathname: router.pathname,
      });

      if (typeof mixpanel !== 'undefined' && environment === 'production') {
        mixpanel.track(eventParams.event_name as string, eventParams);
      }
    } else {
      // payment comment 5
      // setStripeModal(true);
      await initializeRazorpay({
        account: 'razorpay_2',
        totalAmount: order?.grand_total || 0,
        razorpayOrderId: order?.transactionId || '',
        orderId: order?.orderId || '',

        userData: {
          ...userData,
          name: data?.userData?.name ?? userData?.name ?? '',
          email: data?.userData?.email ?? userData?.email ?? '',
        } as User,
        setVisible: data.setVisible,
        paymentType: 'razor-pay',
        cartId: (cartData[0]?.cartId as number) || Number(cartId),
        isCartPage: true,
      });

      const eventParams: EventDetails = {
        event_name: 'retry_payment',
        source: 'global',
        source_type: 'orders',
        source_id: order?.orderId as string,
        sub_source: null,
        sub_source_id: null,
        unit: order?.currency_code || 'INR',
        currency: order?.currency_code || 'INR',
        value: order?.grand_total,
        meta: {
          click_source: 'retry_popup',
          payment_mode: order?.payment_type === 'razorpay' ? 'prepaid' : 'cod',
        },
      };

      logEvent({
        eventDetails: eventParams,
        userData: userData,
        pathname: router.pathname,
      });

      if (typeof mixpanel !== 'undefined' && environment === 'production') {
        mixpanel.track(eventParams.event_name as string, eventParams);
      }
    }
  };

  const hasDigitalBook = order?.orderDetails.some(
    (detail) => detail.book_type === 'digital',
  );

  return (
    <>
      <Modal
        open={visible}
        maskClosable={false}
        closable={false}
        centered={true}
        footer={null}
        styles={{
          mask: {
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(8px)',
          },
        }}
        className='md:max-w-[505px] customModalNoPadding customMaskModal'
        onCancel={handleCloseModal}
        keyboard={false}
      >
        <div className='flex flex-col gap-y-3 bg-[#000000E5] border rounded-2xl border-[#303030] px-6 pb-12'>
          <span className='text-center flex justify-center mt-16 xs:mt-20 md:mt-24'>
            <ExclamationMarkCart className='text-[#DC9646] w-16 h-16' />
          </span>
          <p className='text-[#A0A0A0] text-center font-semibold text-base mt-2'>
            Your Order Failed
          </p>
          <p className='text-lg font-semibold text-white text-center px-5 md:px-16 mt-8'>
            Don’t worry, all your filled information is safe with us.
          </p>
          <p className='md:px-24 text-center mt-0.5 text-[#A7A7A7]'>
            <ShouldRender check={!hasDigitalBook && typeof pre === 'undefined'}>
              <ShouldRender check={countryState.selectedCountry?.code === 'IN'}>
                Choose Cash on Delivery (COD) or
              </ShouldRender>
            </ShouldRender>{' '}
            Retry to complete your order.
          </p>
          <div className='flex flex-col-reverse md:flex-row gap-y-2 xs:gap-x-3 md:gap-y-0 md:gap-x-3.5 mt-8 xs:mt-8 md:mt-10 relative'>
            <ShouldRender check={hasDigitalBook}>
              <button
                onClick={() => {
                  handleCloseModal();
                  const eventParams: EventDetails = {
                    event_name: 'cancel_order',
                    source: 'global',
                    source_type: 'orders',
                    source_id: order?.orderId as string,
                    sub_source: null,
                    sub_source_id: null,
                    unit: order?.currency_code || 'INR',
                    currency: order?.currency_code || 'INR',
                    value: order?.grand_total,
                    meta: {
                      click_source: 'retry_popup',
                      payment_mode:
                        order?.payment_type === 'razorpay' ? 'prepaid' : 'cod',
                    },
                  };

                  logEvent({
                    eventDetails: eventParams,
                    userData: userData,
                    pathname: router.pathname,
                  });
                  if (
                    typeof mixpanel !== 'undefined' &&
                    environment === 'production'
                  ) {
                    mixpanel.track(
                      eventParams.event_name as string,
                      eventParams,
                    );
                  }

                  router.replace('/my-orders');
                }}
                className={clsx(
                  hasDigitalBook ? 'md:w-1/2' : '',
                  'text-sm md:text-base hover:text-white whitespace-nowrap cursor-pointer underline font-medium text-[#737373] text-center mt-4 underline-offset-2',
                )}
              >
                Don’t place my Order
              </button>
            </ShouldRender>
            <ButtonDefault
              size={2}
              roundedSm={true}
              disabled={loadingOrder}
              className={clsx(
                typeof pre !== undefined ? 'w-full' : 'md:w-5/12',
                ' border border-[#2C2C2C]',
              )}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                handlePayment();
              }}
              variant={
                hasDigitalBook ? ButtonVariants.WHITE : ButtonVariants.GRAY
              }
            >
              <span
                className={clsx(
                  hasDigitalBook ? 'text-black' : 'text-white',
                  'flex text-base gap-x-2 md:px-4 md:py-0.5 whitespace-nowrap',
                )}
              >
                Retry Order
              </span>
            </ButtonDefault>
            <ShouldRender
              check={
                !hasDigitalBook &&
                typeof pre === 'undefined' &&
                !hasNoCOD &&
                countryState.selectedCountry?.code === 'IN'
              }
            >
              <ButtonDefault
                size={2}
                roundedSm={true}
                disabled={
                  convertCodLoading || (order?.grand_total || 0) > cod_limit
                }
                onClick={() => {
                  convertToCod();
                }}
                spinColor='black'
                enableScaling={false}
                className={clsx(
                  (order?.grand_total || 0) > cod_limit && 'mb-5',
                  'md:w-full',
                )}
                loading={convertCodLoading}
                variant={ButtonVariants.WHITE}
              >
                <span className='flex text-sm md:text-base text-black font-bold gap-x-2 md:px-4 py-0.5 whitespace-nowrap'>
                  {!convertCodLoading ? 'Complete with COD' : 'Completing'}
                  {convertCodLoading && <DotLoader />}
                </span>
              </ButtonDefault>
            </ShouldRender>
            <ShouldRender check={(order?.grand_total || 0) > cod_limit}>
              <p className='text-xs md:w-7/12 right-0 text-textGrayLight top-[52px] pl-5 absolute'>
                COD unavailable for amount {cod_limit}
              </p>
            </ShouldRender>
          </div>

          <ShouldRender check={!hasDigitalBook}>
            <p
              onClick={() => {
                handleCloseModal();
                const eventParams: EventDetails = {
                  event_name: 'cancel_order',
                  source: 'global',
                  source_type: 'orders',
                  source_id: order?.orderId as string,
                  sub_source: null,
                  sub_source_id: null,
                  unit: order?.currency_code || 'INR',
                  currency: order?.currency_code || 'INR',
                  value: order?.grand_total,
                  meta: {
                    click_source: 'retry_popup',
                    payment_mode:
                      order?.payment_type === 'razorpay' ? 'prepaid' : 'cod',
                  },
                };

                logEvent({
                  eventDetails: eventParams,
                  userData: userData,
                  pathname: router.pathname,
                });
                if (
                  typeof mixpanel !== 'undefined' &&
                  environment === 'production'
                ) {
                  mixpanel.track(eventParams.event_name as string, eventParams);
                }

                router.replace('/my-orders');
              }}
              className='text-sm md:text-base hover:text-white cursor-pointer underline font-medium text-[#737373] text-center mt-7 underline-offset-2'
            >
              Don’t place my Order
            </p>
          </ShouldRender>
        </div>
      </Modal>
      {/* {orderApiData?.payment_details?.client_secret && stripeModal ? (
        <StripeMain
          options={options}
          amount={Math.round(orderApiData.grand_total * 100)}
          currency={orderApiData.currency_code.toLowerCase()}
          displayAmount={orderApiData.grand_total}
          clientSecret={orderApiData?.payment_details?.client_secret}
          open={stripeModal}
          onSuccess={async (paymentIntent) => {
            try {
              if (!process?.env?.NEXT_PUBLIC_STRIPE_PUBLIC_KEY) {
                notify('NEXT_PUBLIC_STRIPE_PUBLIC_KEY not found');
                return;
              }
              await createRazorpayPayment({
                order_id: orderApiData.id,
                razorpay_order_id: paymentIntent.id,
                razorpay_payment_id: paymentIntent.id,
                razorpay_signature: paymentIntent.client_secret as string,
              });

              const productIds = (orderApiData?.order_details || [])
                .map((detail: any) => detail?.product?.id)
                .join(',');

              getSyncBookListApi();
              if (typeof window !== 'undefined') {
                router.replace({
                  pathname: '/order-successful',
                  query: {
                    orderId: orderApiData.id,
                    product_ids: productIds,
                    callPurchased: 'purchased',
                  },
                });
                window.history.replaceState(null, '', '/order-successful');
              }
            } catch (error: any) {
              notify(formateErrorObject(error));
            } finally {
              setStripeModal(false);
            }
          }}
          onCancel={() => {
            setStripeModal(false);
            data.setVisible(true);
          }}
        />
      ) : (
        <></>
      )} */}
    </>
  );
};

export default CloseModal;
